import { format } from "date-fns";

import Bank from "../bank/Bank";
import EmployeeDetails from "../personal/EmployeeDetails";
import RoleDetails from "../role/RoleDetails";
import SalaryDetails from "../salary-details/SalaryDetails";
import TaxDetails from "../tax-details/TaxDetails";
// import TaxCredit from './tax-credit/TaxCredit';
// import EmployeeDashboard from "./EmployeeDashboard";
import AttachmentOfEarnings from "../attachment-of-earnings/AttachmentOfEarnings";
import EmployeeAuditLogs from "../audit-logs/EmployeeAuditLogs";
import Children from "../children/Children";
import EmployeeNotes from "../notes/EmployeeNotes";
import PensionDetails from "../pension/PensionDetails";
import PreviousEarnings from "../previous-earnings/PreviousEarnings";
import TotalsToDate from "../previous-earnings/TotalsToDate";
import { orderBy } from "lodash";
import EmployeePayslips from "../EmployeePayslips";
import TimesheetTable from "@/components/timesheets/TimesheetTable";
import EmployeeLeave from "../EmployeeLeave";
import EmployeeTimesheets from "../EmployeeTimesheets";
import EmployeeSalary from "../salary-details/EmployeeSalary";

const today = format(new Date(), "yyyy-MM-dd");

export function getEmployeeTabs({ occupations, hasMultiRole, type }) {
	const employmentTabs = [];

	if (!hasMultiRole) {
		employmentTabs.push(RoleTab, SalaryTab, PensionTab);
	} else {
		occupations = orderBy(occupations, ["active"], ["desc"]);
		const occupationTabs = occupations.map(({ value, end, start, role }) => {
			const occupationIsTerminated = !end || end >= today;
			const occupationLink = `/roles/${value}`;
			const paths = occupationLink.split("/").filter((path) => path);

			return {
				...RoleTab,
				show: ({ tabPaths = [] }) => {
					const matchingPaths = tabPaths.filter((p, i) => p === paths[i])?.length;
					if (matchingPaths === paths.length) return true;
					return occupationIsTerminated;
				},
				name: role,
				link: occupationLink,
				subTabs: [
					{ ...RoleTab, link: `${RoleTab.link}/${value}/role`, componentParams: { occupation: value } },
					{ ...SalaryTab, link: `${RoleTab.link}/${value}/salary`, componentParams: { occupation: value } },
					{ ...PensionTab, link: `${RoleTab.link}/${value}/pension`, componentParams: { occupation: value } },
				],
			};
		});
		employmentTabs.push(OccupationsTab, ...occupationTabs);
	}

	return [
		OverviewTab,
		{
			name: "Personal",
			tabs: [PersonalTab, DependantsTab, TaxTab, CisVerificationTab, BankTab, NotesTab],
		},
		{
			name: "Employment",
			tabs: [...employmentTabs, PayslipsTab, LeaveTab, TimesheetsTab],
		},
		{
			name: "Other",
			tabs: [PreviousEarningsTab, CommitmentsTab],
		},
		{
			name: "Security",
			tabs: [AuditLogsTab],
		},
	];
}

export function getEmployeeRoutes(tabs, props) {
	if (!tabs && props) tabs = getEmployeeTabs(props);

	return tabs
		.flatMap((tab) => {
			if (tab.tabs) {
				return tab.tabs;
			}
			return tab;
		})
		.flatMap((tab) => {
			if (tab.subTabs) {
				return tab.subTabs;
			}
			return tab;
		});
}

const OverviewTab = {
	name: "Overview",
	icon: "WindowIcon",
	link: "/",
	// component: EmployeeDashboard
};

const OccupationsTab = {
	link: "/occupations",
	name: "Occupations",
	icon: "BriefcaseIcon",
	component: RoleDetails,
	show: ({ hasMultiRole }) => hasMultiRole,
};

const RoleTab = {
	link: "/roles",
	name: "Role",
	icon: "OccupationIcon",
	component: RoleDetails,
};

const PersonalTab = {
	link: "/personal",
	name: "Personal",
	icon: "UserIcon",
	default: true,
	component: EmployeeDetails,
};

const DependantsTab = {
	link: "/dependants",
	name: "Dependants",
	icon: "UserGroupIcon",
	type: ["default"],
	show: ({ hasChildren }) => hasChildren,
	component: Children,
};

const TaxTab = {
	link: "/tax",
	name: "Tax",
	show: ({ type }) => type !== "cis",
	icon: "ReceiptPercentIcon",
	component: TaxDetails,
};

const CisVerificationTab = {
	link: "/cis-verification",
	name: "Verify CIS",
	show: ({ type }) => type === "cis",
	icon: "ReceiptPercentIcon",
	component: TaxDetails,
};

const BankTab = {
	link: "/bank",
	name: "Bank",
	icon: "BuildingLibraryIcon",
	component: Bank,
};

const NotesTab = {
	link: "/notes",
	name: "Notes",
	icon: "ArchiveBoxIcon",
	component: EmployeeNotes,
};

const SalaryTab = {
	link: "/salary",
	name: "Salary",
	icon: "BanknotesIcon",
	component: EmployeeSalary,
};

const PensionTab = {
	link: "/pension",
	name: "Pension",
	icon: "PiggyBankIcon",
	show: ({ jurisdiction, type }) => ["UK"].includes(jurisdiction) && type !== "cis",
	component: PensionDetails,
};

const PayslipsTab = {
	link: "/payslips",
	name: "Payslips",
	icon: "NewspaperIcon",
	component: EmployeePayslips,
};

const LeaveTab = {
	link: "/leave",
	name: "Leave",
	icon: "CalendarIcon",
	component: EmployeeLeave,
};

const TimesheetsTab = {
	link: "/timesheets",
	name: "Timesheets",
	icon: "ClockIcon",
	component: EmployeeTimesheets,
};

const PreviousEarningsTab = {
	link: "/previous-earnings",
	name: "Previous Earnings",
	icon: "BackwardIcon",
	show: ({ type }) => type !== "cis",
	component: [PreviousEarnings, TotalsToDate],
};

const CommitmentsTab = {
	link: "/commitments",
	name: "Commitments",
	icon: "CubeTransparentIcon",
	show: ({ jurisdiction }) => ["UK"].includes(jurisdiction),
	// consider setup of parameters in case we want to split AoE/Commitments in multiple blocks
	componentParams: {
		details: {
			0: {
				name: "Commitment",
				namePlural: "Commitments",
				// filter: (i) => i.type === "Reducing Balance Loan",
				// defaultType: "Reducing Balance Loan",
			},
		},

	},
	component: [
		AttachmentOfEarnings,
	],
};

const AuditLogsTab = {
	link: "/audit-logs",
	name: "Audit Logs",
	icon: "ShieldCheckIcon",
	component: EmployeeAuditLogs,
};
